// FilterSelection.tsx - Consumer repo with immediate filtering support

import Filter from './Filter';
import { Box, Divider, Typography, useMediaQuery } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useEffect, useState, useMemo } from 'react';

// Updated filter category interface for string-based filters
interface FilterCategory {
  title: string;
  available: string[];
  filtered: string[];
  list: string[];
  setList: React.Dispatch<React.SetStateAction<string[]>>;
  keyName: string;
  paramKey: string;
}

// Updated props interface - matches your existing structure
interface FilterSelectionProps {
  // Available options
  availableColours: string[];
  availableDesigns: string[];
  availableTypes: string[];
  availableBrands: string[];
  availableMaterials: string[];
  availableStandards: string[];
  availableSizes: string[];
  availableShapes: string[];

  // Filtered options
  filteredColours: string[];
  filteredDesigns: string[];
  filteredTypes: string[];
  filteredBrands: string[];
  filteredMaterials: string[];
  filteredStandards: string[];
  filteredSizes: string[];
  filteredShapes: string[];

  // Selected values
  colourList: string[];
  designList: string[];
  typesList: string[];
  brandList: string[];
  materialList: string[];
  standardsList: string[];
  sizeList: string[];
  shapeList: string[];

  // State setters
  setColourList: React.Dispatch<React.SetStateAction<string[]>>;
  setDesignList: React.Dispatch<React.SetStateAction<string[]>>;
  setTypesList: React.Dispatch<React.SetStateAction<string[]>>;
  setBrandList: React.Dispatch<React.SetStateAction<string[]>>;
  setMaterialList: React.Dispatch<React.SetStateAction<string[]>>;
  setStandardsList: React.Dispatch<React.SetStateAction<string[]>>;
  setSizeList: React.Dispatch<React.SetStateAction<string[]>>;
  setShapeList: React.Dispatch<React.SetStateAction<string[]>>;

  // Other props
  handleCheckboxChange: (
    elem: string,
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    list: string[],
    paramKey: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  applicationsList: string[];
  itemGroup: string;
  isLoading?: boolean;
}

const FilterSelection: React.FC<FilterSelectionProps> = ({
                                                           // Available options
                                                           availableColours,
                                                           availableDesigns,
                                                           availableTypes,
                                                           availableBrands,
                                                           availableMaterials,
                                                           availableStandards,
                                                           availableSizes,
                                                           availableShapes,

                                                           // Filtered options
                                                           filteredColours,
                                                           filteredDesigns,
                                                           filteredTypes,
                                                           filteredBrands,
                                                           filteredMaterials,
                                                           filteredStandards,
                                                           filteredSizes,
                                                           filteredShapes,

                                                           // Selected values
                                                           colourList,
                                                           designList,
                                                           typesList,
                                                           brandList,
                                                           materialList,
                                                           standardsList,
                                                           sizeList,
                                                           shapeList,

                                                           // State setters
                                                           setColourList,
                                                           setDesignList,
                                                           setTypesList,
                                                           setBrandList,
                                                           setMaterialList,
                                                           setStandardsList,
                                                           setSizeList,
                                                           setShapeList,

                                                           // Other props
                                                           handleCheckboxChange,
                                                           applicationsList,
                                                           itemGroup,
                                                           isLoading = false
                                                         }) => {
  // Initialize visibility state for all filter sections
  const [visibility, setVisibility] = useState<{ [key: string]: boolean }>({
    Colour: false,
    Design: false,
    Types: false,
    Brand: false,
    Material: false,
    Standards: false,
    Size: false,
    Shape: false,
  });

  const isMobile = useMediaQuery('(max-width:600px)');

  // Toggle all sections closed on mobile
  useEffect(() => {
    if (isMobile) {
      setVisibility(prevState =>
        Object.keys(prevState).reduce((acc, key) => ({
          ...acc,
          [key]: false
        }), {})
      );
    }
  }, [isMobile]);

  const filterCategories: FilterCategory[] = useMemo(() => [
    {
      title: 'Brand',
      available: availableBrands,
      filtered: filteredBrands,
      list: brandList,
      setList: setBrandList,
      keyName: 'brand',
      paramKey: 'brand',
    },
    {
      title: 'Design',
      available: availableDesigns,
      filtered: filteredDesigns,
      list: designList,
      setList: setDesignList,
      keyName: 'design_style',
      paramKey: 'design_style',
    },
    {
      title: 'Standards',
      available: availableStandards,
      filtered: filteredStandards,
      list: standardsList,
      setList: setStandardsList,
      keyName: 'web_standards',
      paramKey: 'standards',
    },
    {
      title: 'Colour',
      available: availableColours,
      filtered: filteredColours,
      list: colourList,
      setList: setColourList,
      keyName: 'web_colour',
      paramKey: 'colours',
    },
    {
      title: 'Types',
      available: availableTypes,
      filtered: filteredTypes,
      list: typesList,
      setList: setTypesList,
      keyName: 'web_type',
      paramKey: 'types',
    },
    {
      title: 'Material',
      available: availableMaterials,
      filtered: filteredMaterials,
      list: materialList,
      setList: setMaterialList,
      keyName: 'web_materials',
      paramKey: 'material',
    },
    {
      title: 'Size',
      available: availableSizes,
      filtered: filteredSizes,
      list: sizeList,
      setList: setSizeList,
      keyName: 'web_size',
      paramKey: 'sizes',
    },
    {
      title: 'Shape',
      available: availableShapes,
      filtered: filteredShapes,
      list: shapeList,
      setList: setShapeList,
      keyName: 'web_shape',
      paramKey: 'shapes',
    },
  ], [
    availableBrands, availableDesigns, availableStandards, availableColours, availableTypes, availableMaterials, availableSizes, availableShapes,
    filteredBrands, filteredDesigns, filteredStandards, filteredColours, filteredTypes, filteredMaterials, filteredSizes, filteredShapes,
    brandList, designList, standardsList, colourList, typesList, materialList, sizeList, shapeList,
    setBrandList, setDesignList, setStandardsList, setColourList, setTypesList, setMaterialList, setSizeList, setShapeList
  ]);

  // Memoize the getFilteredOptions function to prevent recreating on every render
  const getFilteredOptions = useMemo(() => (available: string[], filtered: string[], category: string) => {
    // If no application selected or no available items, return available array
    if (!applicationsList?.length || !available?.length) {
      // For Types category, filter out the "-" value
      if (category === 'Types') {
        return available.filter(item => item !== '-');
      }
      return available;
    }

    // If filtered is empty or undefined but we have available items, show all
    if (!filtered?.length && available?.length) {
      // For Types category, filter out the "-" value
      if (category === 'Types') {
        return available.filter(item => item !== '-');
      }
      return available;
    }

    // Since we're now dealing with strings only, we can just filter based on matching values
    const lowerFiltered = filtered.map((value: string) => value.toLowerCase());
    return available.filter((item) => {
      // Skip "-" values for Types category
      if (category === 'Types' && item === '-') {
        return false;
      }
      return lowerFiltered.includes(item.toLowerCase());
    });
  }, [applicationsList]);

  return (
    <Box
      sx={{
        opacity: isLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
        position: 'relative'
      }}
    >
      {/* Loading indicator overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px'
          }}
        >
          <Typography
            sx={{
              color: '#666',
              fontSize: '14px',
              fontStyle: 'italic'
            }}
          >
            Updating filters...
          </Typography>
        </Box>
      )}

      {filterCategories.map((category) => {
        const filteredOptions = getFilteredOptions(
          category.available,
          category.filtered,
          category.title
        );

        // Skip rendering if no options or shape for non-004 itemGroup
        if (!filteredOptions?.length ||
          (category.title === 'Shape' && itemGroup !== '004')) {
          return null;
        }

        return (
          <Box key={category.title}>
            <Divider sx={{ backgroundColor: '#000000', m: '1rem 0' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography fontWeight="bold" fontSize="20px" mb={2}>
                {category.title}
              </Typography>
              <Box
                onClick={() => setVisibility(prev => ({
                  ...prev,
                  [category.title]: !prev[category.title]
                }))}
                sx={{ cursor: 'pointer' }}
              >
                {visibility[category.title] ?
                  <KeyboardArrowDownIcon /> :
                  <KeyboardArrowUpIcon />
                }
              </Box>
            </Box>
            {visibility[category.title] && (
              <Filter
                filterType={filteredOptions}
                filteredOptions={category.filtered}
                handleCheckboxChange={handleCheckboxChange}
                list={category.list}
                setList={category.setList}
                keyName={category.keyName}
                paramKey={category.paramKey}
              />
            )}
          </Box>
        );
      })}

      <Divider sx={{ backgroundColor: '#000000', m: '1rem 0' }} />
    </Box>
  );
};

export default FilterSelection;